import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ReactElement, useEffect, useState } from "react";

export default function VerticalDragAndDrop<T extends { id?: string }>({
  items,
  onChange,
  itemRenderer,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  itemRenderer: (item: T) => ReactElement;
}) {
  const [itemsCached, setItemsCached] = useState<T[]>(items);
  useEffect(() => {
    if (itemsCached !== items) {
      setItemsCached(items);
    }
  }, [items]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const overId = over?.id;
    const activeId = active.id;
    if (activeId === overId) {
      return;
    }
    const activeIndex = itemsCached.findIndex((item) => item.id === activeId);
    const overIndex = itemsCached.findIndex((item) => item.id === overId);
    const newItemList = arrayMove(itemsCached, activeIndex, overIndex);
    setItemsCached(newItemList);
    onChange(newItemList);
  }
  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={
          itemsCached as {
            id: string;
          }[]
        }
        strategy={verticalListSortingStrategy}
      >
        {itemsCached.map((item) => {
          if (!item.id) return <></>;
          return (
            <SortableItem id={item.id} key={item.id}>
              {itemRenderer(item)}
            </SortableItem>
          );
        })}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem(props: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: props.id,
  });
  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    // transition: transition
  };

  return (
    <div className="relative  group" ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        className="w-6 cursor-grab absolute -left-3 top-1 invisible shadow group-hover:visible p-0.5 rounded-sm bg-white z-100"
      >
        <svg
          viewBox="0 0 20 20"
          focusable="false"
          aria-hidden="true"
          className="text-gray-200"
        >
          <path d="M6.75 3.5a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1h-1Z"></path>
          <path d="M6.75 8.5a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1h-1Z"></path>
          <path d="M5.75 14.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1Z"></path>
          <path d="M12.25 3.5a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1h-1Z"></path>
          <path d="M11.25 9.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1Z"></path>
          <path d="M12.25 13.5a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1h-1Z"></path>
        </svg>
      </div>
      {props.children}
    </div>
  );
}
